import { Breadcrumbs } from '@/components/Breadcrumbs';
import { PageContainer } from '@/components/PageContainer';
import { toaster } from '@/components/ui/toaster';
import { trpc } from '@/lib/trpc';
import { Spinner } from '@televet/kibble-ui/Spinner';
import { Text } from '@televet/kibble-ui/Text';
import { Flex } from '@televet/kibble-ui/chakra';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { BlueprintEditor } from './-components/BlueprintEditor';
import { QueryErrorAlert } from './-components/QueryErrorAlert';
import { readErrorCode } from './-lib/errors';

export const Route = createFileRoute('/_authenticated/_testing-tools/blueprints/$blueprintId')({
  beforeLoad: () => ({ pageTitle: 'Blueprint' }),
  component: LabBlueprintPage,
});

function NotFound() {
  return (
    <Flex direction="column" gap={4} w="100%">
      <Breadcrumbs items={[{ label: 'Blueprints', to: '/blueprints' }, { label: 'Not found' }]} />
      <Text data-testid="lab-blueprint-not-found">
        This blueprint does not exist. It may have been deleted.
      </Text>
    </Flex>
  );
}

function LabBlueprintPage() {
  const { blueprintId } = Route.useParams();

  const { data, isLoading, error, refetch } = useQuery({
    ...trpc.labDataBlueprint.findOne.queryOptions({ id: blueprintId }),
    retry: false,
    refetchOnWindowFocus: false,
  });

  const reload = async () => {
    const result = await refetch();
    if (result.error) {
      toaster.error({ title: 'Could not reload the blueprint', description: result.error.message });
      return undefined;
    }
    return result.data;
  };

  if (isLoading) {
    return (
      <PageContainer>
        <Spinner label="Loading blueprint" />
      </PageContainer>
    );
  }

  // A failed background refetch keeps the cached row, so an open draft is never swapped for an error.
  if (data) {
    return (
      <PageContainer>
        <BlueprintEditor key={data.id} blueprint={data} onReload={reload} />
      </PageContainer>
    );
  }

  if (readErrorCode(error) === 'NOT_FOUND') {
    return (
      <PageContainer>
        <NotFound />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <QueryErrorAlert
        title="Could not load this blueprint"
        message={error?.message ?? 'The request failed.'}
        onRetry={() => void refetch()}
        data-testid="lab-blueprint-detail-load-error"
      />
    </PageContainer>
  );
}
